import { usePresentationMode } from '@euler/app/components/PresentationModeContext';
import { useAuthContext } from '@euler/app/flows/auth/context';
import { kDefaultIntroVideo } from '@euler/assets';
import { oemConfig } from '@euler/config/oem';
import { useBehaviorSubject, useCreation } from '@euler/utils/hooks';
import { ResizeMode, Video } from 'expo-av';
import { memo, useEffect, useState } from 'react';
import { StyleSheet, TouchableWithoutFeedback, View } from 'react-native';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  map,
  mergeWith,
  of,
} from 'rxjs';

export const appTouch$ = new BehaviorSubject<boolean>(true);

const kDefaultTimeout = 3600 * 1000; // 1 hour
const kEnabled = false;

export const WiredScreenSaver = memo(() => {
  const mode = usePresentationMode();
  const { authState$ } = useAuthContext();
  const [authState] = useBehaviorSubject(authState$);
  const isAuthenticated = authState.isAuthenticated === true;

  const controller$ = useCreation(
    () =>
      mode === 'presentation' && isAuthenticated
        ? appTouch$.pipe(
            mergeWith(
              appTouch$.pipe(
                debounceTime(kDefaultTimeout),
                map(() => false),
              ),
            ),
          )
        : of(true),
    [isAuthenticated, mode],
  );

  const [showIntroVideo, setShowIntroVideo] = useState(false);

  useEffect(() => {
    const sub = controller$.pipe(distinctUntilChanged()).subscribe(value => {
      setShowIntroVideo(!value);
    });
    return () => sub.unsubscribe();
  }, [controller$]);

  if (!showIntroVideo || !kEnabled) return null;

  return (
    <View
      style={StyleSheet.absoluteFill}
      css={`
        background-color: #000;
        z-index: 10000;
      `}
    >
      <Video
        source={
          oemConfig.introVideo?.source?.dark ??
          oemConfig.introVideo?.source?.light ??
          kDefaultIntroVideo
        }
        shouldPlay
        isLooping
        isMuted
        resizeMode={ResizeMode.CONTAIN}
        style={StyleSheet.absoluteFill}
        pointerEvents="none"
      />
      <TouchableWithoutFeedback onPress={() => setShowIntroVideo(false)}>
        <View style={StyleSheet.absoluteFill} />
      </TouchableWithoutFeedback>
    </View>
  );
});
